import * as React from 'react'

import { waitForTitle } from '@/helpers/waitForTitleSettled'
import { useProductPrice } from '@/hooks/bff/prices/getPrices/useProductPrice'
import { useTrackEvent } from '@/hooks/useTrackEvent'
import { getProductPageItemList } from '@/lib/eventBus/selectedItemList'
import { TrackingTopic } from '@/lib/eventBus/trackingTopics'

import type { Product } from '@/gen/ecom'

const isProductPageTitle = (productName?: string) => (title: string) =>
  productName ? title.includes(productName) : title !== ''

export const useProductViewTracking = (product: Product, isPriceShown: boolean) => {
  const { track } = useTrackEvent()
  const { priceData, isLoading: isPriceLoading } = useProductPrice(product.code)
  const isPriceSettled = !isPriceShown || !isPriceLoading
  const lastTrackedProductCode = React.useRef<string | undefined>(undefined)

  React.useEffect(() => {
    if (!isPriceSettled || lastTrackedProductCode.current === product.code) {
      return
    }

    const controller = new AbortController()

    void waitForTitle(isProductPageTitle(product.name), controller.signal).then(() => {
      if (controller.signal.aborted) {
        return
      }

      lastTrackedProductCode.current = product.code
      track(TrackingTopic.ItemViewed, { product, priceData, listContext: getProductPageItemList(product.code) })
    })

    return () => controller.abort()
  }, [product, priceData, isPriceSettled, track])
}
